// frontend/src/LaunchAssignmentGrid.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCheckSquare, FaSync } from "react-icons/fa";
import { apiFetch } from "./api";

// ---- helpers ----
const toLocalYmd = (value = new Date()) => {
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const addDays = (dateStr, delta) => {
  const base = new Date(`${dateStr}T00:00:00`);
  base.setDate(base.getDate() + delta);
  return toLocalYmd(base);
};

const formatShortDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const weekday = date.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
  const md = date.toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
  return `${weekday} ${md}`;
};

const formatTime12 = (timeStr) => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return "";
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${m || "00"} ${period}`;
};

const today = () => toLocalYmd(new Date());

const PRESETS = [
  { label: "2 weeks", days: 14 },
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
];
const DEFAULT_RANGE_DAYS = 30;

function hexToRgba(hex, alpha) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const FALLBACK_LINEUP_COLOR = "#38bdf8";
const FALLBACK_MANUAL_COLOR = "#34d399";

export default function LaunchAssignmentGrid({ coach }) {
  const [fromDate, setFromDate] = useState(() => today());
  const [toDate, setToDate] = useState(() => addDays(today(), DEFAULT_RANGE_DAYS - 1));
  const [activePreset, setActivePreset] = useState(30);

  const [coaches, setCoaches] = useState([]);
  const [practices, setPractices] = useState([]);
  const [assignments, setAssignments] = useState({});

  const [launches, setLaunches] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingCell, setSavingCell] = useState(null);

  // inline date editor (only active when NOT in select mode)
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const selectRef = useRef(null);

  // cell-level bulk selection
  const [selectMode, setSelectMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState(new Set()); // Set of "coachId-practiceId"
  const [bulkLaunch, setBulkLaunch] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const tableRef = useRef(null);

  // ---- load data ----
  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [gridData, boatConfig] = await Promise.all([
        apiFetch(`/coach-launch-assignments?from=${fromDate}&to=${toDate}`),
        apiFetch("/boat-config").catch(() => ({ launches: [] })),
      ]);
      setCoaches(Array.isArray(gridData.coaches) ? gridData.coaches : []);
      setPractices(Array.isArray(gridData.practices) ? gridData.practices : []);
      setAssignments(gridData.assignments || {});
      setSelectedCells(new Set());

      const launchList = Array.isArray(boatConfig?.launches) ? boatConfig.launches : [];
      setLaunches(
        launchList
          .map((l) => {
            if (typeof l === "string") return { value: l, statusColor: null, statusName: null };
            return {
              value: l.value || "",
              statusColor: l.statusColor || null,
              statusName: l.statusName || null,
            };
          })
          .filter((l) => l.value)
      );
    } catch (e) {
      setError(e?.message || "Failed to load launch assignments");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (editingCell && selectRef.current) {
      selectRef.current.focus();
    }
  }, [editingCell]);

  // Exit select mode when switching date range
  useEffect(() => {
    setSelectMode(false);
    setSelectedCells(new Set());
    setBulkLaunch("");
  }, [fromDate, toDate]);

  // ---- save single cell ----
  const saveCell = useCallback(
    async (coachId, practiceId, launch) => {
      const key = `${coachId}-${practiceId}`;
      setSavingCell(key);
      try {
        await apiFetch("/coach-launch-assignments", {
          method: "PUT",
          body: JSON.stringify({ coachId, practiceId, launch }),
        });
        if (launch) {
          const info = launches.find((l) => l.value === launch) || {};
          setAssignments((prev) => {
            const next = { ...prev };
            const cId = String(coachId);
            const pId = String(practiceId);
            if (!next[cId]) next[cId] = {};
            next[cId][pId] = {
              launch,
              source: "manual",
              statusColor: info.statusColor || null,
              statusName: info.statusName || null,
            };
            return next;
          });
        } else {
          const fresh = await apiFetch(`/coach-launch-assignments?from=${fromDate}&to=${toDate}`);
          setAssignments(fresh.assignments || {});
        }
      } catch (e) {
        alert(e?.message || "Failed to save assignment");
      } finally {
        setSavingCell(null);
      }
    },
    [fromDate, toDate, launches]
  );

  // ---- cell open/close ----
  const openCell = (coachId, practiceId) => {
    const cId = String(coachId);
    const pId = String(practiceId);
    const current = assignments[cId]?.[pId]?.launch || "";
    setEditingCell({ coachId, practiceId });
    setEditValue(current);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { coachId, practiceId } = editingCell;
    const cId = String(coachId);
    const pId = String(practiceId);
    const current = assignments[cId]?.[pId]?.launch || "";
    setEditingCell(null);
    if (editValue !== current) {
      saveCell(coachId, practiceId, editValue);
    }
  };

  const cancelEdit = () => setEditingCell(null);

  // ---- select mode ----
  const enterSelectMode = () => {
    setEditingCell(null);
    setSelectMode(true);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedCells(new Set());
    setBulkLaunch("");
  };

  const toggleCell = (coachId, practiceId) => {
    const key = `${coachId}-${practiceId}`;
    setSelectedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Select / deselect all cells in a coach row
  const toggleRow = (coachId, practiceList) => {
    const rowKeys = practiceList.map((p) => `${coachId}-${p.id}`);
    const allSelected = rowKeys.every((k) => selectedCells.has(k));
    setSelectedCells((prev) => {
      const next = new Set(prev);
      if (allSelected) rowKeys.forEach((k) => next.delete(k));
      else rowKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  // Select / deselect all cells in a practice column
  const toggleColumn = (practiceId, coachList) => {
    const colKeys = coachList.map((c) => `${c.id}-${practiceId}`);
    const allSelected = colKeys.every((k) => selectedCells.has(k));
    setSelectedCells((prev) => {
      const next = new Set(prev);
      if (allSelected) colKeys.forEach((k) => next.delete(k));
      else colKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  // ---- bulk apply ----
  const applyBulkAssign = async () => {
    if (!bulkLaunch || selectedCells.size === 0) return;
    setBulkSaving(true);
    const info = launches.find((l) => l.value === bulkLaunch) || {};
    try {
      await Promise.all(
        [...selectedCells].map((key) => {
          const [coachId, practiceId] = key.split("-");
          return apiFetch("/coach-launch-assignments", {
            method: "PUT",
            body: JSON.stringify({ coachId, practiceId, launch: bulkLaunch }),
          });
        })
      );
      setAssignments((prev) => {
        const next = { ...prev };
        for (const key of selectedCells) {
          const [coachId, practiceId] = key.split("-");
          if (!next[coachId]) next[coachId] = {};
          next[coachId][practiceId] = {
            launch: bulkLaunch,
            source: "manual",
            statusColor: info.statusColor || null,
            statusName: info.statusName || null,
          };
        }
        return next;
      });
      exitSelectMode();
    } catch (e) {
      alert(e?.message || "Failed to bulk-assign launches");
    } finally {
      setBulkSaving(false);
    }
  };

  // ---- derived ----
  const visiblePractices = useMemo(() => practices, [practices]);

  const isEditing = (coachId, practiceId) =>
    editingCell?.coachId === coachId && editingCell?.practiceId === practiceId;

  const cellKey = (coachId, practiceId) => `${coachId}-${practiceId}`;

  const usedStatuses = useMemo(() => {
    const seen = new Map();
    for (const byPractice of Object.values(assignments)) {
      for (const { statusColor, statusName } of Object.values(byPractice)) {
        if (statusName && !seen.has(statusName)) {
          seen.set(statusName, statusColor);
        }
      }
    }
    return [...seen.entries()].map(([name, color]) => ({ name, color }));
  }, [assignments]);

  return (
    <div className="stack" style={{ gap: 12 }}>
      {/* Pill bar */}
      <div className="lineup-pill-bar">
        <div className="lineup-pill-filters">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              className={`btn-filter${activePreset === p.days ? " active" : ""}`}
              type="button"
              onClick={() => {
                const f = today();
                const t = addDays(f, p.days - 1);
                setFromDate(f);
                setToDate(t);
                setActivePreset(p.days);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="lineup-pill-actions">
          <button
            className={`btn${selectMode ? " primary" : " ghost"}`}
            type="button"
            onClick={selectMode ? exitSelectMode : enterSelectMode}
            title={selectMode ? "Exit select mode" : "Select cells to bulk-assign a launch"}
            style={{ gap: 6, display: "flex", alignItems: "center" }}
          >
            <FaCheckSquare style={{ fontSize: 13 }} />
            {selectMode ? "Cancel Select" : "Select"}
          </button>
          <button
            className="btn ghost"
            type="button"
            onClick={loadData}
            disabled={loading}
          >
            <FaSync style={{ fontSize: 12, marginRight: 4, opacity: loading ? 0.5 : 1 }} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <div className="lineup-pill-filter-panel">
          <div className="date-range-filter">
            <label className="date-range-filter-label">
              From
              <input
                type="date"
                className="input"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setActivePreset(null); }}
              />
            </label>
            <label className="date-range-filter-label">
              To
              <input
                type="date"
                className="input"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setActivePreset(null); }}
              />
            </label>
          </div>
        </div>
      </div>

      {selectMode && (
        <div style={{ fontSize: 12, color: "var(--accent)", paddingLeft: 4 }}>
          Select mode — click cells to select, click a coach name to select their row, click a date header to select the column.
          {selectedCells.size > 0 && (
            <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>
              {" "}{selectedCells.size} cell{selectedCells.size !== 1 ? "s" : ""} selected.
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="card" style={{ padding: 12 }}>
          <span className="help" style={{ color: "var(--danger)" }}>{error}</span>
        </div>
      )}

      {/* Grid */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
            Loading…
          </div>
        ) : visiblePractices.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
            No practices found in this date range.
          </div>
        ) : (
          <div
            ref={tableRef}
            style={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "calc(100vh - 260px)",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: "100%",
                tableLayout: "fixed",
                userSelect: selectMode ? "none" : undefined,
              }}
            >
              <colgroup>
                <col style={{ width: 140, minWidth: 120 }} />
                {visiblePractices.map((p) => (
                  <col key={p.id} style={{ width: 110, minWidth: 90 }} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  {/* Top-left corner */}
                  <th
                    style={{
                      padding: "8px 10px",
                      textAlign: "left",
                      background: "var(--bg-elevated)",
                      borderBottom: "2px solid var(--border)",
                      borderRight: "1px solid var(--border)",
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      color: "var(--muted)",
                    }}
                  >
                    Coach
                  </th>
                  {visiblePractices.map((p) => {
                    const dateLabel = formatShortDate(p.date);
                    const timeLabel = formatTime12(p.startTime || "");
                    const nameLabel =
                      p.abbrevName || (p.name && p.name.length <= 12 ? p.name : null) || "";
                    const colKeys = coaches.map((c) => `${c.id}-${p.id}`);
                    const colAllSelected = colKeys.length > 0 && colKeys.every((k) => selectedCells.has(k));
                    return (
                      <th
                        key={p.id}
                        onClick={selectMode ? () => toggleColumn(p.id, coaches) : undefined}
                        style={{
                          padding: "6px 6px",
                          textAlign: "center",
                          background: colAllSelected
                            ? "rgba(59,130,246,0.12)"
                            : "var(--bg-elevated)",
                          borderBottom: "2px solid var(--border)",
                          borderRight: "1px solid var(--border)",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          verticalAlign: "bottom",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--text)",
                          cursor: selectMode ? "pointer" : undefined,
                        }}
                      >
                        <div style={{ fontWeight: 700, color: colAllSelected ? "var(--accent)" : "var(--text-strong)" }}>
                          {dateLabel}
                        </div>
                        {timeLabel && (
                          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>
                            {timeLabel}
                          </div>
                        )}
                        {nameLabel && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--accent)",
                              marginTop: 1,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {nameLabel}
                          </div>
                        )}
                        {(p.team || (p.squads && p.squads.length > 0)) && (
                          <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 2, lineHeight: 1.4, whiteSpace: "normal", wordBreak: "break-word" }}>
                            {p.team && <span style={{ display: "block" }}>{p.team}</span>}
                            {(p.squads || []).map((sq) => (
                              <span key={sq} style={{ display: "block" }}>{sq}</span>
                            ))}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {coaches.map((c, rowIdx) => {
                  const cId = String(c.id);
                  const rowBg = rowIdx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)";
                  const rowKeys = visiblePractices.map((p) => `${c.id}-${p.id}`);
                  const rowAllSelected = rowKeys.length > 0 && rowKeys.every((k) => selectedCells.has(k));
                  return (
                    <tr key={c.id}>
                      {/* Coach name */}
                      <td
                        onClick={selectMode ? () => toggleRow(c.id, visiblePractices) : undefined}
                        style={{
                          padding: "8px 10px",
                          fontWeight: 600,
                          fontSize: 13,
                          color: rowAllSelected ? "var(--accent)" : "var(--text-strong)",
                          borderBottom: "1px solid var(--border)",
                          borderRight: "1px solid var(--border)",
                          position: "sticky",
                          left: 0,
                          background: rowAllSelected
                            ? "rgba(59,130,246,0.10)"
                            : "var(--card)",
                          zIndex: 2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 140,
                          cursor: selectMode ? "pointer" : undefined,
                        }}
                      >
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.name}
                        </div>
                        {(c.team || c.squad) && (
                          <div style={{ fontSize: 10, fontWeight: 400, color: "var(--muted)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {[c.team, c.squad].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>

                      {/* Practice cells */}
                      {visiblePractices.map((p) => {
                        const pId = String(p.id);
                        const assignmentData = assignments[cId]?.[pId];
                        const launch = assignmentData?.launch || "";
                        const source = assignmentData?.source || null;
                        const statusColor = assignmentData?.statusColor || null;
                        const statusName = assignmentData?.statusName || null;
                        const key = cellKey(c.id, p.id);
                        const saving = savingCell === key;
                        const editing = isEditing(c.id, p.id);
                        const isSelected = selectedCells.has(key);

                        let cellBg = rowBg;
                        if (isSelected) {
                          cellBg = "rgba(59,130,246,0.18)";
                        } else if (editing) {
                          cellBg = "rgba(59,130,246,0.08)";
                        } else if (launch) {
                          const rgba = hexToRgba(statusColor, 0.12);
                          if (rgba) {
                            cellBg = rgba;
                          } else {
                            cellBg = source === "lineup"
                              ? "rgba(56,189,248,0.06)"
                              : "rgba(52,211,153,0.06)";
                          }
                        }

                        const labelColor = launch
                          ? statusColor || (source === "lineup" ? FALLBACK_LINEUP_COLOR : FALLBACK_MANUAL_COLOR)
                          : undefined;

                        const cellBorder = isSelected
                          ? "2px solid var(--accent)"
                          : launch && source === "lineup"
                            ? `1px dashed ${statusColor || "var(--accent)"}`
                            : undefined;

                        return (
                          <td
                            key={p.id}
                            style={{
                              padding: "4px 6px",
                              borderBottom: "1px solid var(--border)",
                              borderRight: "1px solid var(--border)",
                              textAlign: "center",
                              verticalAlign: "middle",
                              background: cellBg,
                              outline: cellBorder,
                              outlineOffset: -2,
                              minWidth: 90,
                              position: "relative",
                              cursor: selectMode ? "pointer" : "pointer",
                            }}
                            onClick={() => {
                              if (selectMode) {
                                toggleCell(c.id, p.id);
                              } else if (!editing && !saving) {
                                openCell(c.id, p.id);
                              }
                            }}
                          >
                            {/* Selected overlay checkmark */}
                            {selectMode && isSelected && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: 2,
                                  right: 3,
                                  fontSize: 10,
                                  color: "var(--accent)",
                                  lineHeight: 1,
                                  pointerEvents: "none",
                                }}
                              >
                                ✓
                              </span>
                            )}

                            {!selectMode && editing ? (
                              <div
                                style={{ display: "flex", alignItems: "center", gap: 4 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <select
                                  ref={selectRef}
                                  className="input"
                                  style={{
                                    flex: 1,
                                    padding: "2px 4px",
                                    fontSize: 12,
                                    minWidth: 0,
                                    height: 28,
                                  }}
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") commitEdit();
                                    if (e.key === "Escape") cancelEdit();
                                  }}
                                  onBlur={commitEdit}
                                >
                                  <option value="">— Clear —</option>
                                  {launches.map((l) => (
                                    <option key={l.value} value={l.value}>
                                      {l.value}
                                      {l.statusName ? ` · ${l.statusName}` : ""}
                                    </option>
                                  ))}
                                  {launch && !launches.find((l) => l.value === launch) && (
                                    <option value={launch}>{launch}</option>
                                  )}
                                </select>
                              </div>
                            ) : saving ? (
                              <span style={{ fontSize: 11, color: "var(--muted)" }}>…</span>
                            ) : launch ? (
                              <div style={{ position: "relative" }}>
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: labelColor,
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    padding: "0 2px",
                                  }}
                                  title={[
                                    launch,
                                    statusName ? `Status: ${statusName}` : null,
                                    source === "lineup" ? "(from lineup)" : "(manual)",
                                  ].filter(Boolean).join(" · ")}
                                >
                                  {launch}
                                </span>
                                {statusName && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: statusColor || "var(--muted)",
                                      opacity: 0.85,
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {statusName}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--border)",
                                  opacity: 0.6,
                                }}
                              >
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      {!loading && visiblePractices.length > 0 && (
        <div
          className="row"
          style={{ gap: 16, flexWrap: "wrap", paddingLeft: 4, paddingBottom: 4, alignItems: "center" }}
        >
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                background: "rgba(52,211,153,0.15)",
                border: "1.5px solid var(--success)",
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Manually assigned</span>
          </div>

          {usedStatuses.length > 0 && (
            <>
              <span style={{ fontSize: 12, color: "var(--border)", userSelect: "none" }}>|</span>
              {usedStatuses.map(({ name, color }) => (
                <div key={name} className="row" style={{ gap: 6, alignItems: "center" }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: color || "var(--muted)",
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>{name}</span>
                </div>
              ))}
            </>
          )}

          <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: "auto" }}>
            {selectMode ? "Click cells, coach names, or date headers to select." : "Click any cell to assign a launch."}
          </span>
        </div>
      )}

      {/* Bulk assign bar — shown when cells are selected */}
      {selectMode && selectedCells.size > 0 && (
        <div
          style={{
            position: "sticky",
            bottom: 16,
            zIndex: 20,
            background: "var(--card)",
            border: "1.5px solid var(--accent)",
            borderRadius: 10,
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)", whiteSpace: "nowrap" }}>
            {selectedCells.size} cell{selectedCells.size !== 1 ? "s" : ""} selected
          </span>
          <span style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>— assign to:</span>
          <select
            className="input"
            value={bulkLaunch}
            onChange={(e) => setBulkLaunch(e.target.value)}
            style={{ fontSize: 13, padding: "4px 8px", height: 32, minWidth: 140 }}
          >
            <option value="">Pick a launch…</option>
            {launches.map((l) => (
              <option key={l.value} value={l.value}>
                {l.value}{l.statusName ? ` · ${l.statusName}` : ""}
              </option>
            ))}
          </select>
          <button
            className="btn primary"
            style={{ height: 32, padding: "0 16px", fontSize: 13, whiteSpace: "nowrap" }}
            disabled={!bulkLaunch || bulkSaving}
            onClick={applyBulkAssign}
          >
            {bulkSaving ? "Saving…" : "Apply"}
          </button>
          <button
            className="btn ghost"
            style={{ height: 32, padding: "0 12px", fontSize: 13 }}
            disabled={bulkSaving}
            onClick={exitSelectMode}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
